import React, { FC, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useApolloClient } from '@apollo/client';
import { Item } from '@baemingo/order-status-events/dist/types';
import { useI18n, useSelector } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { Station } from '~Types';
import { ItemWithCourseType } from '~Scenes/EventedKitchenDisplay';
import { EnrichedOrder } from '~Scenes/EventedKitchenDisplay/view';
import ItemName from './ItemName';
import { itemInBong } from '~Scenes/POS/Queries/__queries__';
import { ITEM_FRAGMENT } from '~Scenes/POS/Queries/query';
import { stationMatchesItem } from '../utils';
import CompletedOrdersLog, { getBusinessDayStart } from './CompletedOrdersLog';

const Wrapper = styled.div`
  width: 360px;
  min-width: 360px;
  max-width: 360px;
  border-left: 1px solid var(--colorBlueGray1000);
  overflow-y: auto;
  background: var(--colorBlueGray1300);
`;

const PanelHeader = styled.div`
  padding: 16px 20px 12px;
  border-bottom: 1px solid var(--colorBlueGray1000);
`;

const PanelTabs = styled.div`
  display: flex;
  gap: 16px;
`;

const PanelTitle = styled.div<{ active?: boolean }>`
  color: ${({ active }) => (active ? 'var(--colorWhite)' : 'var(--colorBlueGray500)')};
  cursor: pointer;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Section = styled.div<{ faded?: boolean }>`
  padding: 12px 20px;
  opacity: ${({ faded }) => (faded ? 0.5 : 1)};

  & + & {
    border-top: 1px solid var(--colorBlueGray1000);
  }
`;

const SectionTitle = styled.div`
  color: var(--colorBlueGray500);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  color: var(--colorBlueGray200);
  padding: 6px 0;
  gap: 12px;
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  &:last-child {
    border-bottom: none;
  }
`;

const Count = styled.div`
  min-width: 32px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  font-weight: 700;
  font-size: 13px;
  background: var(--colorBlueGray1000);
  border-radius: 6px;
  color: var(--colorWhite);
`;

const EmptyState = styled.div`
  color: var(--colorBlueGray500);
  font-size: 13px;
  padding: 8px 0;
`;

interface Props {
  allOrders: EnrichedOrder[];
  regularKDSOrders: EnrichedOrder[];
  upcomingOrders: EnrichedOrder[];
  itemsWithCourseType: ItemWithCourseType[];
  venueId: string;
}

type Aggregated = {
  key: string;
  itemId: string;
  count: number;
};

const SummaryPanel: FC<Props> = ({ allOrders, regularKDSOrders, upcomingOrders, venueId }) => {
  const { i18n } = useI18n();
  const [tab, setTab] = useState<'summary' | 'logs'>('summary');
  // The log fetches the day's events when it mounts, so it stays mounted once opened
  const [logsOpened, setLogsOpened] = useState(false);
  // Same value for the whole business day, changes at 04:00
  const businessDayStart = getBusinessDayStart();
  const [storage, setStorage] = useStorage();
  const stations = useSelector((state) => state.stations || []);
  const client = useApolloClient();

  const selectedStation: Station | undefined = stations.find((s: Station) => s.id === storage.selectedKDSStation);

  // Try to read item name from Apollo cache for sorting
  const getItemName = (itemId: string): string => {
    const fragment = client.readFragment<itemInBong>({
      id: `Item${itemId}`,
      fragment: ITEM_FRAGMENT,
    });
    return fragment ? i18n(fragment.titleLang) : itemId;
  };

  const aggregate = (orders: EnrichedOrder[]) => {
    const map = new Map<string, Aggregated>();
    orders.forEach((order) => {
      order.order.items.forEach((item: Item) => {
        const key = [item.itemId, item.comment || '', item.variations.join('|'), item.removedVariations.join('|')].join('::');
        const existing = map.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          map.set(key, { key, itemId: item.itemId, count: 1 });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => getItemName(a.itemId).localeCompare(getItemName(b.itemId)));
  };

  const otherStationIds = useMemo(() => {
    const ids = storage.KDSOtherStationsFilter || [];
    if (ids.length > 0) {
      return ids;
    }
    return stations.filter((s: Station) => s.id !== selectedStation?.id).map((s: Station) => s.id);
  }, [stations, selectedStation, storage.KDSOtherStationsFilter]);

  const otherStationsOrders = useMemo(() => {
    if (!selectedStation?.showAllItems) {
      return [] as EnrichedOrder[];
    }

    const relevantStations = stations.filter((s: Station) => otherStationIds.includes(s.id));
    return allOrders
      .map((order) => ({
        ...order,
        order: {
          ...order.order,
          items: order.order.items.filter((item: Item) => relevantStations.some((station: Station) => stationMatchesItem(station, item))),
        },
      }))
      .filter((order) => order.order.items.length > 0 && order.order.fired !== false);
  }, [allOrders, selectedStation, stations, otherStationIds]);

  const currentItems = useMemo(() => aggregate(regularKDSOrders), [regularKDSOrders]);
  const upcomingItems = useMemo(() => aggregate(upcomingOrders), [upcomingOrders]);
  const otherItems = useMemo(() => aggregate(otherStationsOrders), [otherStationsOrders]);

  const toggleStation = (stationId: string) => {
    const current = storage.KDSOtherStationsFilter || [];
    const next = current.includes(stationId)
      ? current.filter((id: string) => id !== stationId)
      : [...current, stationId];
    setStorage({ KDSOtherStationsFilter: next });
  };

  return (
    <Wrapper>
      <PanelHeader>
        <PanelTabs>
          <PanelTitle active={tab === 'summary'} onClick={() => setTab('summary')}>
            {i18n('Settings.KDS.SummaryPanel') || 'Summary'}
          </PanelTitle>
          <PanelTitle
            active={tab === 'logs'}
            onClick={() => {
              setTab('logs');
              setLogsOpened(true);
            }}>
            {i18n('KDS.Logs') || 'Logs'}
          </PanelTitle>
        </PanelTabs>
      </PanelHeader>

      {logsOpened && (
        <div style={{ display: tab === 'logs' ? 'block' : 'none' }}>
          <CompletedOrdersLog
            // Remounts the log when the business day changes, the panel renders on every KDS update
            key={businessDayStart}
            venueId={venueId}
            active={tab === 'logs'}
            businessDayStart={businessDayStart}
          />
        </div>
      )}

      {tab === 'summary' && (
        <>
          <Section>
            <SectionTitle>{i18n('KDS.CurrentOrders') || 'Current'}</SectionTitle>
            {currentItems.length === 0 && (
              <EmptyState>{i18n('KDS.NoItems') || 'No items'}</EmptyState>
            )}
            {currentItems.map((item) => (
              <Row key={item.key}>
                <Count>{item.count}</Count>
                <ItemName id={item.itemId} />
              </Row>
            ))}
          </Section>

          {selectedStation?.showAllItems && (
            <Section>
              <SectionTitle>{i18n('KDS.OtherStations')}</SectionTitle>
              {stations
                .filter((s: Station) => s.id !== selectedStation.id)
                .map((station: Station) => {
                  const active = otherStationIds.includes(station.id);
                  return (
                    <Row
                      key={station.id}
                      onClick={() => toggleStation(station.id)}
                      style={{ cursor: 'pointer', opacity: active ? 1 : 0.5 }}>
                      <span>{station.name}</span>
                      <Count
                        style={{
                          background: active ? 'var(--colorBlueGray1000)' : 'transparent',
                        }}>
                        {active ? '✓' : ''}
                      </Count>
                    </Row>
                  );
                })}
              {otherItems.map((item) => (
                <Row key={item.key}>
                  <ItemName id={item.itemId} />
                  <Count>{item.count}</Count>
                </Row>
              ))}
            </Section>
          )}

          {upcomingItems.length > 0 && (
            <Section faded>
              <SectionTitle>{i18n('KDS.Upcoming') || 'Upcoming'}</SectionTitle>
              {upcomingItems.map((item) => (
                <Row key={item.key}>
                  <Count>{item.count}</Count>
                  <ItemName id={item.itemId} />
                </Row>
              ))}
            </Section>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default SummaryPanel;
